import { BackendError, Service } from '@genial/services';
import {
  EquityEntity,
  EquitySuccessPagedListResponse,
  MetaResponse,
} from '@genial/services/dist/models';
import { selector, selectorFamily } from 'recoil';

import { blankMetaResponse } from '../../../utils/blankMetaResponse';
import { customerItemState } from '../../customer/atom';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../../master/selector';
import {
  productItemState,
  equityOffersBySearchTermParamsState,
  equityOffersParamsState,
  equityOffersBySearchTermState,
  equityOffersAllState,
  equityOffersOperationTypeState,
  equityPositionOffersParamsState,
  equityPositionOffersAllState,
} from '../atom';
import { screenName } from '../constants';
import { normalizeEquityOffers } from '../normalizer/equity';
import {
  DynamicAssetsQueryParams,
  DynamicMarketStatusProps,
  EquityOfferEntityNormalized,
  EquityOffersBySearchTerm,
  EquityOffersParams,
  EquityOffersPositionParams,
} from '../types';

type EquityOffersQueryData = {
  data: EquityEntity[];
  meta: MetaResponse;
};

export const equityOffersQuery = selectorFamily<
  EquityOffersQueryData | null | undefined,
  { callEndpoint?: boolean }
>({
  key: 'NewApplication/equityOffersQuery',
  get: ({ callEndpoint }) => {
    return async ({ get }) => {
      try {
        if (!callEndpoint) {
          return;
        }

        const equityParams = get(equityOffersParamsState);
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const { data, meta } = await Service.Equities.V1.getAllOffers({
          ...equityParams,
          screenName,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
        });

        const normalizedData = data?.map((item) => item.equity) || [];

        return { data: normalizedData, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    };
  },
});

export const equityPositionOffersQuery = selector({
  key: 'NewApplication/equityPositionOffersQuery',
  get: async ({ get }) => {
    try {
      const equityParams = get(equityPositionOffersParamsState);
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);
      const customer = get(customerItemState).current;

      const { data, meta } = await Service.Equities.V1.getPositionByAccountNumber({
        ...equityParams,
        screenName,
        advisorId: advisorSelected,
        accountNumber: customer.accountNumber,
        partnerId: partnerSelected,
      });

      const normalizedData =
        data?.map((item) => ({ ...item.equity, availableAmount: item.availableAmount })) || [];

      return { data: normalizedData, meta };
    } catch (error) {
      const backendError = error as BackendError;

      if (backendError.statusCode === 404) {
        return { data: [], meta: blankMetaResponse() };
      }

      throw error;
    }
  },
});

export const equityOffersBySearchTermQuery = selector({
  key: 'NewApplication/equityOffersBySearchTermQuery',
  get: async ({ get }) => {
    const equityParams = get(equityOffersBySearchTermParamsState);
    const advisorSelected = get(advisorIdToApisSelector);
    const partnerSelected = get(partnerIdToApisSelector);

    if (equityParams.skipEndpointCall) {
      return;
    }

    const { data, meta } = await Service.Equities.V1.getOffersBySearchTerm({
      ...equityParams,
      screenName,
      advisorId: advisorSelected,
      partnerId: partnerSelected,
    });

    const normalizedData = data?.map((item) => item.equity) || [];

    return { data: normalizedData, meta };
  },
});

export const equityOffersParams = selector({
  key: 'equityOffersParams',
  set: ({ set, get }, newValue) => {
    const currentEquityOffers = get(equityOffersParamsState);
    const value = newValue as Partial<EquityOffersParams>;
    const equityOffers = { ...currentEquityOffers, ...value };

    return set(equityOffersParamsState, equityOffers);
  },
  get: ({ get }) => {
    const item = get(equityOffersParamsState);
    return item;
  },
});

export const equityPositionOffersParams = selector({
  key: 'equityPositionOffersParams',
  set: ({ set, get }, newValue) => {
    const currentEquityPositionOffers = get(equityPositionOffersParamsState);
    const value = newValue as Partial<EquityOffersPositionParams>;
    const equityPositionOffers = { ...currentEquityPositionOffers, ...value };

    return set(equityPositionOffersParamsState, equityPositionOffers);
  },
  get: ({ get }) => {
    const item = get(equityPositionOffersParamsState);
    return item;
  },
});

export const equityOffersBySearchTermParams = selector({
  key: 'equityOffersBySearchTermParams',
  set: ({ set, get }, newValue) => {
    const currentEquityOffersBySearchTerm = get(equityOffersBySearchTermParamsState);
    const value = newValue as Partial<EquityOffersBySearchTerm>;
    const equityOffersBySearchTerm = {
      ...currentEquityOffersBySearchTerm,
      ...value,
    };

    return set(equityOffersBySearchTermParamsState, equityOffersBySearchTerm);
  },
  get: ({ get }) => {
    const item = get(equityOffersBySearchTermParamsState);
    return item;
  },
});

export const updateEquityOffersAllSelector = selector({
  key: 'updateEquityOffersAllSelector',
  set: ({ set }, newValue) => {
    const data = newValue as EquityEntity[];
    return set(equityOffersAllState, data);
  },
  get: ({ get }) => {
    const item = get(equityOffersAllState);
    return item;
  },
});

export const updateEquityPositionOffersAllSelector = selector({
  key: 'updateEquityPositionOffersAllSelector',
  set: ({ set }, newValue) => {
    const data = newValue as EquityEntity[];
    return set(equityPositionOffersAllState, data);
  },
  get: ({ get }) => {
    const item = get(equityPositionOffersAllState);
    return item;
  },
});

export const updateEquityOffersBySearchTermSelector = selector({
  key: 'updateEquityOffersBySearchTermSelector',
  set: ({ set }, newValue) => {
    const data = newValue as EquityEntity[];
    return set(equityOffersBySearchTermState, data);
  },
  get: ({ get }) => {
    const item = get(equityOffersBySearchTermState);
    return item;
  },
});

export const updateEquityProductItemAmountSelector = selector({
  key: 'updateEquityProductItemAmountSelector',
  set: ({ set, get }, newValue) => {
    const currentProductItem = get(productItemState);
    const productItemAmount = newValue as number;
    const data = {
      ...currentProductItem,
      current: {
        ...currentProductItem.current,
        appliedAmount: productItemAmount * currentProductItem.current.priceProductItemSelected!,
        amountProductItemSelected: productItemAmount,
      },
    };
    return set(productItemState, data);
  },
  get: ({ get }) => {
    const item = get(productItemState);
    return item;
  },
});

export const updateEquityProductItemPriceSelector = selector({
  key: 'updateEquityProductItemPriceSelector',
  set: ({ set, get }, newValue) => {
    const currentProductItem = get(productItemState);
    const productItemPrice = newValue as number;
    const data = {
      ...currentProductItem,
      current: {
        ...currentProductItem.current,
        priceProductItemSelected: productItemPrice,
        appliedAmount: productItemPrice * currentProductItem.current.amountProductItemSelected!,
      },
    };
    return set(productItemState, data);
  },
  get: ({ get }) => {
    const item = get(productItemState);
    return item;
  },
});

export const updateEquityProductIdSelector = selector({
  key: 'updateEquityProductIdSelector',
  set: ({ set, get }, productId) => {
    const currentProductItem = get(productItemState);
    const data = {
      ...currentProductItem,
      current: {
        ...currentProductItem.current,
        productId,
      },
    };
    return set(productItemState, data);
  },
  get: ({ get }) => {
    const item = get(productItemState);
    return item;
  },
});

export const equityMarketStatusQuery = selector<DynamicMarketStatusProps>({
  key: 'NewApplication/equityMarketStatusQuery',
  get: async () => {
    const { data } = await Service.Equities.V1.getMarketStatus({
      screenName,
    });

    return {
      isOpen: data?.market.isOpened,
      message: data?.market.status || '',
      originalData: data,
    };
  },
});

export const equityOffersQueryUpdated = selectorFamily<
  { data: EquityOfferEntityNormalized[]; meta: MetaResponse },
  DynamicAssetsQueryParams
>({
  key: 'NewApplication/equityOffersQueryUpdated',
  get: ({ searchTerm, page }) => {
    return async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        let response: EquitySuccessPagedListResponse;

        if (searchTerm) {
          response = await Service.Equities.V1.getOffersBySearchTerm({
            searchTerm,
            page,
            limit: 12,
            screenName,
            advisorId: advisorSelected,
            partnerId: partnerSelected,
          });
        } else {
          response = await Service.Equities.V1.getAllOffers({
            page,
            limit: 12,
            screenName,
            advisorId: advisorSelected,
            partnerId: partnerSelected,
          });
        }

        const normalizedData = normalizeEquityOffers(response.data);

        return { data: normalizedData, meta: response.meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    };
  },
});

export const equitySellOffersQueryUpdated = selectorFamily<
  { data: EquityOfferEntityNormalized[]; meta: MetaResponse },
  DynamicAssetsQueryParams
>({
  key: 'NewApplication/equitySellOffersQueryUpdated',
  get: ({ accountNumber, page }) => {
    return async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const response = await Service.Equities.V1.getPositionByAccountNumber({
          accountNumber,
          page,
          limit: 12,
          screenName,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
        });

        const normalizedData = normalizeEquityOffers(response.data);

        return { data: normalizedData, meta: response.meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    };
  },
});

export const updateEquityOffersOperationTypeSelector = selector({
  key: 'updateEquityOffersOperationTypeSelector',
  get: ({ get }) => {
    const item = get(equityOffersOperationTypeState);
    return item;
  },
  set: ({ set }, newValue) => {
    const data = newValue;
    return set(equityOffersOperationTypeState, data);
  },
});

export const equityBySymbolQuery = selectorFamily<
  { data: EquityEntity },
  { symbol: string; currentScreen?: string }
>({
  key: 'products/equityBySymbolQuery',
  get: ({ symbol, currentScreen }) => {
    return async () => {
      const { data } = await Service.Equities.V1.getOffersSymbols({
        screenName: currentScreen || screenName,
        symbol,
      });

      return { data: data.equity };
    };
  },
});

export const equityOrderBookBySymbolQuery = selectorFamily<
  { data: EquityEntity },
  { symbol: string; currentScreen?: string }
>({
  key: 'equityOrderBookBySymbolQuery',
  get: ({ symbol }) => {
    return async ({ get }) => {
      const { accountNumber } = get(customerItemState).current;
      const operationType = get(equityOffersOperationTypeState);

      const fetchEquityData = async (): Promise<EquityEntity> => {
        if (operationType === 'buy') {
          const { data } = await Service.Equities.V1.getOffersSymbols({
            screenName,
            symbol,
          });
          return data.equity;
        } else {
          const { data } = await Service.Equities.V1.getPositionByAccountNumberSymbol({
            accountNumber,
            screenName,
            symbol,
          });
          return data.equity;
        }
      };

      const equity = await fetchEquityData();
      return { data: equity };
    };
  },
});
